
import { useCallback } from 'react';

export type ImageAnimationVariants = {
  [key: string]: any;
};

export type ImageAnimationOptions = {
  duration?: number;
  delay?: number;
  stagger?: number;
};

/**
 * Shared animation variants for image components to ensure consistent transitions
 */
export const useImageAnimationVariants = (options: ImageAnimationOptions = {}) => {
  const { 
    duration = 0.2, 
    delay = 0, 
    stagger = 0.02 
  } = options;

  const imageCardVariants = useCallback((): ImageAnimationVariants => ({
    hidden: { 
      opacity: 0, 
      scale: 0.9,
      y: 10
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      y: -10,
      transition: {
        duration,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    hover: {
      scale: 1.02,
      y: -2,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    tap: {
      scale: 0.98
    }
  }), [duration]);

  const gridContainerVariants = useCallback((): ImageAnimationVariants => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger,
        delayChildren: delay,
        duration: duration * 0.75
      }
    }
  }), [duration, delay, stagger]);

  const favoriteVariants = useCallback((): ImageAnimationVariants => ({
    hidden: { 
      scale: 0, 
      rotate: -180, 
      opacity: 0 
    },
    visible: { 
      scale: 1, 
      rotate: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 180, // Reduced from 500 for better performance
        damping: 28, // Increased from 20 for faster settling
        delay: 0.05 // Reduced delay for faster response
      }
    },
    exit: { 
      scale: 0, 
      rotate: 180, 
      opacity: 0,
      transition: { 
        duration: 0.2 
      }
    },
    hover: { 
      scale: 1.08, // Reduced scale for subtler effect
      transition: { 
        type: "spring", 
        stiffness: 150, // Reduced from 400 for better performance
        damping: 25 // Increased from 10 for faster settling
      }
    },
    pulse: {
      scale: [1, 1.15, 1], // Reduced scale range for subtler effect
      transition: {
        duration: 2, // Slowed down to reduce CPU usage
        repeat: Infinity,
        ease: "easeInOut",
        repeatDelay: 0.5 // Added delay between pulses to reduce CPU usage
      }
    }
  }), []);

  const imageDetailVariants = useCallback((): ImageAnimationVariants => ({
    hidden: { 
      opacity: 0, 
      scale: 0.95 
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.19, 1, 0.22, 1]
      }
    },
    hover: { 
      scale: 1.03, // Reduced scale for subtler effect
      transition: { 
        type: "spring", 
        stiffness: 170, // Reduced from 300 for better performance
        damping: 30 // Increased from 25 for faster settling
      }
    }
  }), []);

  const navigationVariants = useCallback((): ImageAnimationVariants => ({
    hidden: { 
      opacity: 0 
    },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.2 
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: 0.2 
      }
    }
  }), []);

  return {
    imageCardVariants,
    gridContainerVariants,
    favoriteVariants,
    imageDetailVariants,
    navigationVariants
  };
};

export default useImageAnimationVariants;
